<template>
  <div>
  <v-dialog
    v-model="dialog"
    :fullscreen="$vuetify.display.smAndDown"
    scrollable
    style="overflow-x: hidden !important"
  >
    <v-card>
      <v-card-title class="redColor edit-vehicle-title d-flex align-center ga-2">
        <div class="edit-vehicle-title-text text-white flex-grow-1">
          <span class="text-subtitle-1 font-weight-medium d-block">
            {{ $t("vehicle_entry.edit_car") }}
          </span>
          <span
            v-if="vehicle?.brand?.title || vehicle?.model?.fullDescription"
            class="text-body-2 d-block"
          >
            <template v-if="vehicle?.brand?.title">{{
              vehicle.brand.title
            }}</template>
            <template v-if="vehicle?.model?.fullDescription">
              {{ " " + vehicle.model.fullDescription }}
            </template>
          </span>
        </div>
        <v-btn
          variant="outlined"
          icon
          color="white"
          class="edit-vehicle-close-btn flex-shrink-0"
          size="small"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="flex-wrap">
          <!-- SELECT CAR -->
          <v-col cols="12" md="12" lg="12">
            <select-vehicle
              ref="selectVehicleRef"
              @selected="setSelectedVehicle"
            />
          </v-col>
          <!-- VEHICLE DETAILS -->
          <v-col
            cols="12"
            md="7"
            lg="7"
            :class="{ 'mt-5': $vuetify.display.mdAndUp }"
            class="pl-md-3"
          >
            <vehicle-details
              ref="vehicleDetailsRef"
              @car="selectVehicle"
              @identifier-error="onIdentifierError"
            />
          </v-col>

          <!-- VEHICLE CAD / GRAFICHE -->
          <v-col cols="12" md="5" lg="5">
            <v-tabs
              v-model="partsViewTab"
              color="red-darken-3"
              slider-color="grey-darken-4"
              bg-color="grey-lighten-4"
              class="grafiche-step2-tabs"
            >
              <v-tab value="cad">
                {{ $t("vehicle_entry.step2_tab_cad") }}
              </v-tab>
              <v-tab value="grafiche">
                {{ $t("vehicle_entry.step2_tab_grafiche") }}
              </v-tab>
            </v-tabs>

            <div v-show="partsViewTab === 'cad'" class="mt-3">
              <v-row class="justify-center">
                <v-col cols="12" class="d-flex justify-center">
                  <vehicle-view
                    ref="vehicleView"
                    @position="openPartSelectionDialog"
                  />
                </v-col>
              </v-row>
            </div>
            <div v-show="partsViewTab === 'grafiche'">
              <infocar-grafiche-accordion
                v-if="vehicle"
                ref="graficheAccordion"
                :vehicle="vehicle"
                :show-vehicle-summary="false"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <!-- ACTIONS -->
      <v-card-actions class="pa-3">
        <div class="edit-vehicle-actions w-100">
          <div class="edit-vehicle-actions-group">
            <v-btn
              color="primary"
              variant="flat"
              @click="openPartsList"
            >
              {{ $t("vehicle_entry.parts_list.label") }}
              <v-icon end> mdi-format-list-bulleted-triangle</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="openPhotosDialog"
            >
              {{ $t("vehicle_entry.photos") }}
              <v-icon end> mdi-folder-multiple-image</v-icon>
            </v-btn>
          </div>

          <div class="edit-vehicle-actions-group">
            <v-btn
              :disabled="deleteDocument"
              color="primary"
              variant="flat"
              @click="openPdfInputComponent"
            >
              {{ $t("vehicle_entry.upload_document") }}
              <v-icon end> mdi-file-upload-outline</v-icon>
            </v-btn>
            <input
              ref="pdfInput"
              type="file"
              style="display: none"
              accept="application/pdf"
              @change="onPdfInput"
            />
            <v-btn color="primary" variant="flat" @click="showVideos">
              {{ $t("vehicle_entry.videos") }}
              <v-icon end> mdi-video </v-icon>
            </v-btn>
          </div>

          <div
            v-if="pdf || (vehicle && vehicle.document)"
            class="edit-vehicle-actions-group"
          >
            <v-btn
              v-if="pdf"
              :disabled="deleteDocument"
              color="primary"
              variant="flat"
              @click="viewPdf('new')"
            >
              {{ $t("vehicle_entry.view_document") }}
            </v-btn>
            <v-btn
              v-else-if="vehicle && vehicle.document"
              :disabled="deleteDocument"
              color="primary"
              variant="flat"
              @click="viewPdf('existing')"
            >
              {{ $t("vehicle_entry.view_document") }}
            </v-btn>
            <v-btn
              v-if="vehicle && vehicle.document"
              color="error"
              variant="flat"
              @click="removeDocument"
            >
              {{ $t("vehicle_entry.delete_document") }}
            </v-btn>
          </div>

          <div
            v-if="identifierError"
            class="text-caption text-error w-100 text-end pb-1"
          >
            {{ identifierError }}
          </div>
          <div class="edit-vehicle-actions-group edit-vehicle-actions-footer">
            <v-btn color="error" variant="outlined" @click="close">
              {{ $t("vehicle_entry.close") }}
            </v-btn>
            <v-btn
              color="success"
              variant="outlined"
              :loading="loadingSave"
              @click="save"
            >
              {{ $t("vehicle_entry.save") }}
              <v-icon end> mdi-check-all </v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <!-- HIDDEN COMPONENTS (outside parent dialog to avoid mobile overlay freeze) -->
  <parts-selection
    ref="partsSelection"
    @save="saveParts"
    @close="showDialog"
  />
  <photos
    ref="photos"
    @open-img-dialog="openImgDialog"
    @save="setPhotos"
    @close="showDialog"
  />

  <videos ref="videos" @save="setVideo" @close="showDialog" />

  <img-dialog ref="imgDialog" @close="showPhotos" />
  </div>
</template>

<script>
import SelectVehiclePage from "@/components/VehicleEntry/selectVehicle.vue";
import VehicleDetailsPage from "@/components/VehicleEntry/vehicleDetails.vue";
import VehicleView from "@/components/VehicleEntry/vehicleView.vue";
import InfocarGraficheAccordion from "@/components/VehicleEntry/infocarGraficheAccordion.vue";

import PartsSelection from "@/components/VehicleEntry/partsSelection.vue";
import Photos from "@/components/VehicleEntry/photos.vue";
import Videos from "@/components/VehicleEntry/videos.vue";
import ImgDialog from "@/components/imgDialog.vue";

import postRequest from "../../api/postRequest";
import { useDisplay } from "vuetify";

function dataURLtoBlob(dataUrl) {
  // convert base64 to raw binary data held in a string
  const byteString = atob(dataUrl.split(",")[1]);
  // separate out the mime component
  const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  // create a view into the buffer
  const ia = new Uint8Array(ab);
  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  // write the ArrayBuffer to a blob, and you're done
  return new Blob([ab], { type: mimeString });
}

export default {
  components: {
    "select-vehicle": SelectVehiclePage,
    "vehicle-details": VehicleDetailsPage,
    "vehicle-view": VehicleView,
    "infocar-grafiche-accordion": InfocarGraficheAccordion,
    "parts-selection": PartsSelection,
    photos: Photos,
    videos: Videos,
    "img-dialog": ImgDialog,
  },
  data: () => ({
    dialog: false,
    loadingSave: false,
    vehicle: null,
    pdf: null,
    photosToDelete: [],
    deleteDocument: false,
    partsViewTab: "cad",
    identifierError: "",
  }),
  computed: {
    display() {
      return useDisplay();
    },
  },
  methods: {
    ensureVehicleShapeForDialogs(vehicle) {
      if (!vehicle) return;
      if (!vehicle.parts || typeof vehicle.parts !== "object")
        vehicle.parts = {};
      if (
        vehicle.brand &&
        vehicle.model &&
        vehicle.version &&
        !vehicle.details?.brand
      ) {
        vehicle.details = {
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version,
        };
      }
    },
    open(vehicle) {
      this.partsViewTab = "cad";
      this.ensureVehicleShapeForDialogs(vehicle);
      this.vehicle = vehicle;
      this.$nextTick(() => {
        this.$nextTick(() => {
          const { selectVehicleRef, vehicleDetailsRef } = this.$refs;
          if (selectVehicleRef) {
            selectVehicleRef.setVehicle({
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version,
            });
          }
          if (vehicleDetailsRef) {
            vehicleDetailsRef.setDetails(vehicle);
          }
        });
      });
      this.dialog = true;
    },
    close() {
      const { vehicleDetailsRef, selectVehicleRef } = this.$refs;
      this.$refs.vehicleDetailsRef.error = ""; // Reset error in child
      this.vehicle = null;
      this.pdf = null;
      this.photosToDelete = [];
      this.deleteDocument = false;
      this.$nextTick(() => {
        if (selectVehicleRef) {
          selectVehicleRef.clear();
        }
        if (vehicleDetailsRef) {
          vehicleDetailsRef.clear();
        }
      });
      this.identifierError = "";
      this.$emit("close");
      this.dialog = false;
    },
    onIdentifierError(message) {
      this.identifierError = message || "";
    },
    async save() {
      const { vehicleDetailsRef } = this.$refs;
      this.vehicle.info = vehicleDetailsRef.getDetails();

      if (!vehicleDetailsRef.hasRequiredIdentifiers()) {
        return;
      }
      if (this.$refs.vehicleDetailsRef.validateVIN()) {
        // Continue with save process
      } else {
        return;
      }
      this.loadingSave = true;

      const grafiche = this.$refs.graficheAccordion;
      try {
        if (grafiche?.prepareSlotsForPersist) {
          await grafiche.prepareSlotsForPersist();
        }
      } catch {
        /* save with whatever Grafiche slots are already loaded */
      }
      const partsByCategory =
        grafiche?.getPartsByCategorySnapshot?.() ??
        this.vehicle.partsByCategory ??
        null;
      this.vehicle.partsByCategory = partsByCategory;

      const formData = new FormData();

      formData.append(
        "data",
        JSON.stringify({
          vehicle: {
            institution_id: this.$store.getters.institutionId,
            brand: this.vehicle.details.brand,
            model: this.vehicle.details.model,
            version: this.vehicle.details.version,
            targa: this.vehicle.info.targa,
            vin: this.vehicle.info.vin,
            transmissionType: this.vehicle.info.transmissionType,
            transmissionLayout: this.vehicle.info.transmissionLayout,
            numberOfGears: this.vehicle.info.numberOfGears,
            externalColor: this.vehicle.info.externalColor,
            externalColorCode: this.vehicle.info.externalColorCode,
            internalColor: this.vehicle.info.internalColor,
            internalColorCode: this.vehicle.info.internalColorCode,
            interiorType: this.vehicle.info.interiorType,
            internalState: this.vehicle.info.internalState,
            vehicleCondition: this.vehicle.info.vehicleCondition,
            // frame: this.vehicle.info.frame,
            pra: this.vehicle.info.pra,
            km: this.vehicle.info.km,
            location: this.vehicle.info.location,
            notes: this.vehicle.info.notes,
            registrationYear: this.vehicle.info.registrationYear,
            withdrawnPIVA: this.vehicle.info.withdrawnPIVA,
            workingEngine: this.vehicle.info.workingEngine,
            parts: this.vehicle.parts,
            partsByCategory,
            partsList: this.vehicle.partsList,
            photos: this.vehicle.photos.filter((p) => p && p.image),
            video: this.vehicle.video,
            document: this.vehicle.document,
          },
        }),
      );
      this.vehicle.photos.forEach((p) => {
        if (p && p.blob) formData.append("car-photos", p.blob, p.title);
      });
      if (this.pdf && this.pdf.blob)
        formData.append("car-documents", this.pdf.blob, this.pdf.title);
      if (this.vehicle.video && this.vehicle.video.blob)
        formData.append(
          "car-videos",
          this.vehicle.video.blob,
          this.vehicle.video.title,
        );

      postRequest
        .formData("/car/edit", `car_id=${this.vehicle._id}`, formData)
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          this.loadingSave = false;
          this.$emit("save");
          this.close();
        });
    },
    openPartSelectionDialog(position) {
      const { partsSelection } = this.$refs;
      partsSelection.open(position, this.vehicle, true);
      // this.hideDialog();
    },
    openPhotosDialog() {
      const { photos } = this.$refs;
      photos.open(this.vehicle.photos);
      // this.hideDialog();
    },
    saveParts(data) {
      if (!this.vehicle.parts) this.vehicle.parts = {};
      this.vehicle.parts[data.position] = data.items;
      this.vehicle.parts.comment = data.comment;
    },
    setSelectedVehicle(details) {
      if (details) this.vehicle.details = details;
    },
    openImgDialog(data) {
      const { photos, imgDialog } = this.$refs;
      imgDialog.open(data);
      photos.hide();
    },
    openPartsList() {
      this.$emit("parts-list", {
        vehicle: this.vehicle,
        partsList: this.vehicle.partsList,
        component: "edit",
      });
      // this.hideDialog();
    },
    showPhotos() {
      const { photos } = this.$refs;
      photos.show();
    },
    setPhotos(data) {
      this.photosToDelete = data.photosToDelete;
      this.vehicle.photos = data.photos;
    },
    showVideos() {
      const { videos } = this.$refs;
      videos.open(this.vehicle.video);
     // this.hideDialog();
    },
    setVideo(data) {
      this.vehicle.video = data.video;
    },
    showDialog() {
      this.dialog = true;
    },
    hideDialog() {
      this.dialog = false;
    },
    selectVehicle(data) {
      const { selectVehicle } = this.$refs;
      selectVehicle.setVehicle(data);
    },
    openPdfInputComponent() {
      this.$refs.pdfInput.click();
    },
    onPdfInput(input) {
      const { files } = input.target;
      const fileReader = new FileReader();
      for (const file of files) {
        // eslint-disable-line no-restricted-syntax
        if (file) {
          fileReader.readAsDataURL(file);
        }
      }

      const random = Math.random().toString(36).substring(9);

      fileReader.addEventListener("load", () => {
        const dataUrl = fileReader.result;
        const blob = dataURLtoBlob(dataUrl);
        this.pdf = {
          dataUrl,
          blob,
          title: `${random}${new Date().getTime().toString(16)}.pdf`,
        };
      });
    },
    viewPdf(type) {
      if (type === "new") {
        const objectUrl = window.URL.createObjectURL(this.pdf.blob);
        window.open(objectUrl);
        window.URL.revokeObjectURL(objectUrl);
      } else if (type === "existing") {
        window.open(`${this.$serverUri}/documents?q=${this.vehicle.document}`);
      }
    },
    removeDocument() {
      this.deleteDocument = true;
      this.vehicle.document = null;
    },
  },
};
</script>

<style scoped>
.edit-vehicle-title {
  padding: 12px 16px;
}

.edit-vehicle-title-text {
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}

@media (max-width: 959.98px) {
  .edit-vehicle-title {
    position: relative;
    padding: 12px 52px 12px 16px;
  }

  .edit-vehicle-close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }

  .edit-vehicle-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .edit-vehicle-actions-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    width: 100%;
  }

  .edit-vehicle-actions-group .v-btn {
    flex: 1 1 calc(50% - 8px);
    min-width: 0;
  }

  .edit-vehicle-actions-footer {
    margin-top: 4px;
  }
}

@media (min-width: 960px) {
  .edit-vehicle-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .edit-vehicle-actions-group:not(.edit-vehicle-actions-footer) {
    display: contents;
  }

  .edit-vehicle-actions-footer {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }
}
</style>
